import {
  faCheck,
  faCog,
  faRotate,
  faSkull,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  type AssetFile,
  type IntegrationFile,
  fileProcessingStatusEnum,
} from "kubb";
import type React from "react";
import { useState } from "react";
import { Button } from "../../../../../components/Theme/button";
import UncontrolledTooltip from "../../../../../components/Theme/tooltip";
import {
  useResendAssetFile,
  useResendIntegrationFile,
} from "../../../../../hooks/createEntity";
import { isIntegrationFile } from "../model";

type FileType = AssetFile | IntegrationFile;

interface ResendDataFileButtonProps {
  file: FileType;
  id: string;
  removeQuarantine: boolean;
}

interface ResendButtonInnerProps {
  id: string;
  removeQuarantine: boolean;
  status: string;
  resendFile: {
    update: (body: { remove_quarantine: boolean }) => Promise<any>;
    loading: boolean;
    error?: Error;
  };
}

function ResendButtonInner(props: ResendButtonInnerProps): React.ReactElement {
  const { id, removeQuarantine, status, resendFile } = props;
  const [resent, setResent] = useState(false);

  const handleResendFile = async (): Promise<any> => {
    return resendFile
      .update({ remove_quarantine: removeQuarantine })
      .then(() => {
        setResent(true);
      });
  };

  const iconFa = resent
    ? faCheck
    : resendFile.error
      ? faXmark
      : resendFile.loading
        ? faCog
        : removeQuarantine
          ? faSkull
          : faRotate;

  const disabled = iconFa === faCog || iconFa === faCheck;

  // Only show remove quarantine button for quarantined files
  // Always show resend button (when removeQuarantine is false)
  if (removeQuarantine && status !== fileProcessingStatusEnum.QUARANTINED) {
    return <span className={"fa fa-fw fa-sm"} />;
  }

  return (
    <Button
      plain
      id={`tooltip-file-resend-${id}`}
      onClick={async (): Promise<any> => {
        return await handleResendFile();
      }}
      size={"sm"}
      disabled={disabled}
    >
      {removeQuarantine ? (
        <UncontrolledTooltip
          message={
            resendFile.error
              ? `Error removing quarantine from file ${resendFile.error.message}`
              : "Remove file quarantine"
          }
        >
          <FontAwesomeIcon
            className={"text-brand-dark-700"}
            fixedWidth={true}
            size={"sm"}
            icon={iconFa}
            spin={resendFile.loading}
          />
        </UncontrolledTooltip>
      ) : (
        <UncontrolledTooltip
          message={
            resendFile.error
              ? `Error resending file ${resendFile.error.message}`
              : "Resend file"
          }
        >
          <FontAwesomeIcon
            className={"text-brand-dark-700"}
            fixedWidth={true}
            size={"sm"}
            icon={iconFa}
            spin={resendFile.loading}
          />
        </UncontrolledTooltip>
      )}
    </Button>
  );
}

function ResendIntegrationFileButton(props: {
  file: IntegrationFile;
  id: string;
  removeQuarantine: boolean;
}): React.ReactElement {
  const { file, id, removeQuarantine } = props;
  const resendFile = useResendIntegrationFile(file);

  return (
    <ResendButtonInner
      id={id}
      removeQuarantine={removeQuarantine}
      status={file.status}
      resendFile={resendFile}
    />
  );
}

function ResendAssetFileButton(props: {
  file: AssetFile;
  id: string;
  removeQuarantine: boolean;
}): React.ReactElement {
  const { file, id, removeQuarantine } = props;
  const resendFile = useResendAssetFile(file);

  return (
    <ResendButtonInner
      id={id}
      removeQuarantine={removeQuarantine}
      status={file.status}
      resendFile={resendFile}
    />
  );
}

export default function ResendDataFileButton(
  props: ResendDataFileButtonProps,
): React.ReactElement {
  const { file, id, removeQuarantine } = props;

  if (isIntegrationFile(file)) {
    return (
      <ResendIntegrationFileButton
        file={file}
        id={id}
        removeQuarantine={removeQuarantine}
      />
    );
  }

  return (
    <ResendAssetFileButton
      file={file as AssetFile}
      id={id}
      removeQuarantine={removeQuarantine}
    />
  );
}
